/**
 * Content Review Dashboard — IdeaWorks
 * Visualizes content pipeline stats, approval/decline rates, daily activity.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { TrendingUp, CheckCircle, XCircle, Send, Clock, BarChart2, RefreshCw, Zap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#3dffa0', '#f97316', '#7c5efa', '#60a5fa', '#a68eff'];

function StatCard({ icon: Icon, label, value, color, sub }) {
  return (
    <div className="rounded-2xl p-5 flex flex-col gap-2" style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}20` }}>
          <Icon size={16} style={{ color }} />
        </div>
        <span className="text-xs font-medium" style={{ color: 'var(--llmc-text-muted)' }}>{label}</span>
      </div>
      <div className="text-3xl font-black" style={{ color }}>{value}</div>
      {sub && <div className="text-[11px]" style={{ color: 'var(--llmc-text-muted)' }}>{sub}</div>}
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg px-3 py-2 text-xs" style={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}>
        <p className="font-bold text-white mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>{p.name}: {p.value}</p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await axios.get(`${API}/api/dashboard/stats`);
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="flex-1 flex items-center justify-center">
      <div className="flex items-center gap-3 text-sm" style={{ color: 'var(--llmc-text-muted)' }}>
        <RefreshCw size={16} className="animate-spin" /> Loading dashboard...
      </div>
    </div>
  );

  if (error) return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-red-400 text-sm">{error}</div>
    </div>
  );

  const { discord = {}, queue = {}, daily = [], brand_stats = [], recent_activity = [] } = data || {};

  const statusData = Object.entries(queue.by_status || {}).map(([k, v]) => ({ name: k, value: v }));
  const platformData = Object.entries(queue.by_platform || {}).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <BarChart2 size={20} style={{ color: '#7c5efa' }} />
            <h1 className="text-xl font-black text-white">Content Review Dashboard</h1>
          </div>
          <p className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
            Real-time pipeline metrics · Discord approvals · Daily limits
          </p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
          style={{ background: 'rgba(124,94,250,0.12)', border: '1px solid rgba(124,94,250,0.3)', color: '#a68eff' }}>
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard icon={CheckCircle} label="Approved" value={discord.approved ?? 0} color="#3dffa0" sub="via Discord" />
        <StatCard icon={XCircle} label="Declined" value={discord.declined ?? 0} color="#f97316" sub="via Discord" />
        <StatCard icon={Send} label="Sent for Review" value={discord.sent ?? 0} color="#60a5fa" sub="total sent" />
        <StatCard icon={TrendingUp} label="Approval Rate" value={`${discord.approval_rate ?? 0}%`} color="#a68eff" sub={`${discord.total_reviewed ?? 0} reviewed`} />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Daily activity */}
        <div className="rounded-2xl p-5" style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
          <div className="text-sm font-bold text-white mb-4">Daily Activity (14 days)</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={daily} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{ fill: '#666', fontSize: 10 }} tickFormatter={v => v.slice(5)} />
              <YAxis tick={{ fill: '#666', fontSize: 10 }} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 10, color: '#888' }} />
              <Line type="monotone" dataKey="approved" stroke="#3dffa0" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="declined" stroke="#f97316" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="generated" stroke="#7c5efa" strokeWidth={2} dot={false} strokeDasharray="4 2" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Queue by status */}
        <div className="rounded-2xl p-5" style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
          <div className="text-sm font-bold text-white mb-1">Content Queue</div>
          <div className="text-[10px] mb-4" style={{ color: 'var(--llmc-text-muted)' }}>
            Total: {queue.total ?? 0} items
          </div>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name}: ${value}`} labelStyle={{ fontSize: 10, fill: '#888' }}>
                  {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-48 text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
              No queue data yet
            </div>
          )}
        </div>
      </div>

      {/* Platform + Brand stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* By platform */}
        <div className="rounded-2xl p-5" style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
          <div className="text-sm font-bold text-white mb-4">By Platform</div>
          {platformData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={platformData} margin={{ left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="name" tick={{ fill: '#666', fontSize: 10 }} />
                <YAxis tick={{ fill: '#666', fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Content" fill="#7c5efa" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-40 text-xs" style={{ color: 'var(--llmc-text-muted)' }}>No platform data yet</div>
          )}
        </div>

        {/* Brand daily limits */}
        <div className="rounded-2xl p-5" style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
          <div className="text-sm font-bold text-white mb-4">Brand Daily Limits (Today)</div>
          {brand_stats.length > 0 ? (
            <div className="space-y-3">
              {brand_stats.map(b => (
                <div key={b.id} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: b.color }} />
                  <div className="flex-1 text-xs font-medium text-white">{b.name}</div>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${(b.today_count / b.limit) * 100}%`, background: b.today_count >= b.limit ? '#f97316' : '#3dffa0' }} />
                    </div>
                    <span className="text-[10px] w-10 text-right" style={{ color: b.today_count >= b.limit ? '#f97316' : '#3dffa0' }}>
                      {b.today_count}/{b.limit}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-40 text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
              <div className="text-center">
                <Zap size={24} className="mx-auto mb-2 opacity-40" />
                No brands yet — add brands to track daily limits
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent activity */}
      {recent_activity.length > 0 && (
        <div className="rounded-2xl p-5" style={{ background: 'var(--llmc-surface)', border: '1px solid var(--llmc-border)' }}>
          <div className="text-sm font-bold text-white mb-4">Recent Activity</div>
          <div className="space-y-2">
            {recent_activity.slice(0, 10).map((item, i) => (
              <div key={i} className="flex items-center gap-3 py-1.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${item.event_type === 'content_approved' ? 'bg-green-400' : item.event_type === 'content_declined' ? 'bg-orange-400' : 'bg-blue-400'}`} />
                <span className="text-xs text-white flex-1">{item.event_type?.replace(/_/g, ' ')}</span>
                <span className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>
                  {item.timestamp ? new Date(item.timestamp).toLocaleString() : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
